import { flags } from './flags';

/**
 * Keyboard + mouse input with pointer lock. Keys are KeyboardEvent.code values
 * ('KeyW', 'Space', 'ShiftLeft', ...). "Pressed" edges persist until the next
 * simulation step consumes them, so a tap is never lost between fixed steps.
 */
export class Input {
  private readonly down = new Set<string>();
  private readonly pressedEdges = new Set<string>();
  private mouseDX = 0;
  private mouseDY = 0;
  private wheelDelta = 0;
  private readonly buttonsDown = new Set<number>();
  private readonly buttonEdges = new Set<number>();
  pointerLocked = false;
  /**
   * The lock gives raw mouse motion (`unadjustedMovement`), without the system's pointer
   * acceleration. Only with `?rawmouse=1`: a lock moves a hidden cursor either way, and Chromium
   * on Windows keeps it in the middle of the view only in a plain lock. Under a raw lock it
   * drifts until it is pinned at the view's edge, and at some edges no mouse events come at all:
   * the view stops turning (the bottom-right edge of an Edge window, the rounded bottom-left
   * corner of the Claude app's browser pane). Measured with real mouse input in 2026-10.
   */
  rawMouse = false;
  /** Ask for raw motion (`?rawmouse=1`); otherwise plain locks only. */
  private readonly wantRaw = flags.bool('rawmouse', false);
  /** The browser said raw motion isn't supported: plain locks only. */
  private rawUnsupported = false;
  /** Called when pointer lock is gained/lost (UI shows a click-to-play hint). */
  onLockChange: ((locked: boolean) => void) | null = null;

  constructor(private readonly element: HTMLElement) {
    window.addEventListener('keydown', (e) => {
      if (e.repeat) return;
      // Don't steal keys from the debug panel's text fields.
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;
      this.down.add(e.code);
      this.pressedEdges.add(e.code);
      if (this.pointerLocked && (e.code === 'Space' || e.code.startsWith('Arrow') || e.code === 'Tab')) {
        e.preventDefault();
      }
    });
    window.addEventListener('keyup', (e) => {
      this.down.delete(e.code);
    });
    window.addEventListener('blur', () => {
      this.down.clear();
      this.buttonsDown.clear();
    });
    element.addEventListener('mousedown', (e) => {
      if (!this.pointerLocked) return;
      // Locked without raw motion (it fell back): a click is a chance to upgrade the lock.
      if (this.wantRaw && !this.rawMouse && !this.rawUnsupported) this.lockRaw(false);
      this.buttonsDown.add(e.button);
      this.buttonEdges.add(e.button);
    });
    window.addEventListener('mouseup', (e) => {
      this.buttonsDown.delete(e.button);
    });
    window.addEventListener('mousemove', (e) => {
      if (!this.pointerLocked) return;
      this.mouseDX += e.movementX;
      this.mouseDY += e.movementY;
    });
    window.addEventListener(
      'wheel',
      (e) => {
        if (!this.pointerLocked) return;
        this.wheelDelta += Math.sign(e.deltaY);
      },
      { passive: true },
    );
    document.addEventListener('pointerlockchange', () => {
      this.pointerLocked = document.pointerLockElement === this.element;
      if (!this.pointerLocked) {
        this.down.clear();
        this.rawMouse = false;
      }
      this.onLockChange?.(this.pointerLocked || this.padActive || this.touchActive);
    });
  }

  // ---- touch (phones and tablets; see TouchControls) ----
  private readonly touchDown = new Set<string>();
  /** True while touch controls drive the game (phones have no pointer lock). */
  touchActive = false;

  /** Start or stop playing with touch controls. */
  setTouchActive(on: boolean): void {
    this.touchActive = on;
    if (!on) this.touchDown.clear();
    this.onLockChange?.(on || this.pointerLocked || this.padActive);
  }

  /** Hold or release a virtual key (touch buttons and joystick). */
  setTouchKey(code: string, on: boolean): void {
    if (on) {
      if (!this.touchDown.has(code)) this.pressedEdges.add(code);
      this.touchDown.add(code);
    } else {
      this.touchDown.delete(code);
    }
  }

  /** Add look movement (a touch drag), in mouse pixels. */
  addLook(dx: number, dy: number): void {
    this.mouseDX += dx;
    this.mouseDY += dy;
  }

  requestPointerLock(): void {
    if (!this.wantRaw || this.rawUnsupported) this.lockPlain();
    else this.lockRaw(true);
  }

  /**
   * Lock with raw mouse motion. Fall back to a plain lock only if the browser can't do raw
   * motion at all. Any other failure (clicking back in too soon after Esc, say) leaves the
   * game unlocked for the next click to try again.
   */
  private lockRaw(fallBack: boolean): void {
    const el = this.element as HTMLElement & {
      requestPointerLock(options?: { unadjustedMovement?: boolean }): Promise<void> | void;
    };
    let r: Promise<void> | void;
    try {
      r = el.requestPointerLock({ unadjustedMovement: true });
    } catch {
      // An old browser that throws on the options: no raw motion there.
      this.rawUnsupported = true;
      if (fallBack) this.lockPlain();
      return;
    }
    if (!r || typeof (r as Promise<void>).then !== 'function') {
      // The old, promise-less API ignores the options: this is a plain lock.
      this.rawUnsupported = true;
      return;
    }
    (r as Promise<void>).then(
      () => {
        this.rawMouse = true;
      },
      (e: unknown) => {
        if ((e as { name?: string } | null)?.name !== 'NotSupportedError') return;
        this.rawUnsupported = true;
        console.warn('Raw mouse motion is not supported here: using a plain pointer lock.');
        if (fallBack) this.lockPlain();
      },
    );
  }

  private lockPlain(): void {
    this.rawMouse = false;
    const r = this.element.requestPointerLock() as unknown;
    if (r && typeof (r as Promise<void>).catch === 'function') (r as Promise<void>).catch(() => {});
  }

  isDown(code: string): boolean {
    return this.down.has(code) || this.padDown.has(code) || this.touchDown.has(code);
  }

  /** True if the key went down since the last consumeEdges(). */
  wasPressed(code: string): boolean {
    return this.pressedEdges.has(code);
  }

  isButtonDown(button: number): boolean {
    return this.buttonsDown.has(button) || this.padButtons.has(button);
  }

  wasClicked(button: number): boolean {
    return this.buttonEdges.has(button);
  }

  /** Mouse movement accumulated since the last call (render-rate camera look). */
  takeMouse(out: { x: number; y: number }): { x: number; y: number } {
    out.x = this.mouseDX;
    out.y = this.mouseDY;
    this.mouseDX = 0;
    this.mouseDY = 0;
    return out;
  }

  takeWheel(): number {
    const w = this.wheelDelta;
    this.wheelDelta = 0;
    return w;
  }

  // ---- gamepad (standard mapping) ----
  private readonly padDown = new Set<string>();
  private readonly padButtons = new Set<number>();
  private padPrev: boolean[] = [];
  /** True while a gamepad is driving the game without pointer lock. */
  padActive = false;
  /** Right-stick look speed, in mouse-pixel equivalents per second at full tilt. */
  padLookSpeed = 1100;

  /** Poll connected gamepads once per frame (called by the engine). */
  pollGamepads(dt: number): void {
    const pads = typeof navigator !== 'undefined' && navigator.getGamepads ? navigator.getGamepads() : [];
    const pad = pads ? [...pads].find((p) => p?.connected) : undefined;
    this.padDown.clear();
    this.padButtons.clear();
    if (!pad) return;
    const b = (i: number) => pad.buttons[i]?.pressed ?? false;
    // Menu starts / stops playing with the pad (no pointer lock needed).
    if (b(9) && !this.padPrev[9]) {
      this.padActive = !this.padActive;
      this.onLockChange?.(this.padActive || this.pointerLocked);
    } else if (
      !this.padActive &&
      !this.pointerLocked &&
      pad.buttons.some((x, i) => x.pressed && !this.padPrev[i])
    ) {
      // Any button on the start screen starts playing too.
      this.padActive = true;
      this.onLockChange?.(true);
    }
    this.padPrev = pad.buttons.map((x) => x.pressed);
    if (!this.padActive) return;

    const dead = (v: number) => (Math.abs(v) < 0.15 ? 0 : (v - Math.sign(v) * 0.15) / 0.85);
    const lx = dead(pad.axes[0] ?? 0);
    const ly = dead(pad.axes[1] ?? 0);
    if (ly < -0.3) this.padDown.add('KeyW');
    if (ly > 0.3) this.padDown.add('KeyS');
    if (lx < -0.3) this.padDown.add('KeyA');
    if (lx > 0.3) this.padDown.add('KeyD');
    // Look: a gentle curve so small tilts aim precisely.
    const rx = dead(pad.axes[2] ?? 0);
    const ry = dead(pad.axes[3] ?? 0);
    this.mouseDX += Math.sign(rx) * rx * rx * this.padLookSpeed * dt;
    this.mouseDY += Math.sign(ry) * ry * ry * this.padLookSpeed * 0.75 * dt;

    const keys: [number, string][] = [
      [0, 'Space'], // A: jump / float up
      [1, 'KeyC'], // B: crouch / float down
      [2, 'KeyF'], // X: throw
      [3, 'KeyV'], // Y: fly
      [4, 'KeyL'], // LB: lantern
      [5, 'ShiftLeft'], // RB: sprint
      [10, 'ShiftLeft'], // L3: sprint
      [11, 'KeyO'], // R3: over-the-shoulder view
      [8, 'KeyR'], // View: back to start
      [12, 'KeyT'], // D-pad up: speed up time
      [13, 'KeyM'], // D-pad down: sound
    ];
    for (const [i, code] of keys) {
      if (!b(i)) continue;
      this.padDown.add(code);
      if (!this.padWas.has(code)) this.pressedEdges.add(code);
    }
    // Triggers act as mouse buttons: LT aims, RT throws.
    if (b(6)) this.padButtons.add(2);
    if (b(7)) {
      this.padButtons.add(0);
      if (!this.padWasButton) this.buttonEdges.add(0);
    }
    this.padWasButton = b(7);
    this.padWas = new Set(this.padDown);
  }

  private padWas = new Set<string>();
  private padWasButton = false;
  /** Called by the engine after a simulation step has seen the edges. */
  consumeEdges(): void {
    this.pressedEdges.clear();
    this.buttonEdges.clear();
  }
}
